import { useState, useEffect, useCallback, useRef } from 'react';
import { schoolApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Plus, Search, Trash2, Edit, Loader2, User, Camera, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolStudentsPage() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [view, setView] = useState('list'); // 'list' | 'progression'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', matricule: '', birth_date: '', gender: 'M',
    class_id: '', parent_phone: '', student_type: 'quranic', is_boarding: false,
  });

  const loadData = useCallback(async () => {
    try {
      const params = filterClass !== 'all' ? { class_id: filterClass } : {};
      const [studentsRes, classesRes] = await Promise.all([
        schoolApi.getStudents(params),
        schoolApi.getClasses(),
      ]);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);

      if (view === 'progression') {
        const evalParams = filterClass !== 'all' ? { class_id: filterClass } : {};
        const evalsRes = await schoolApi.getEvaluations(evalParams);
        setEvaluations(evalsRes.data);
      }
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filterClass, view]);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (key) => (e) => setForm({ ...form, [key]: typeof e === 'string' ? e : e.target.value });

  const resetForm = () => {
    setForm({ first_name: '', last_name: '', matricule: '', birth_date: '', gender: 'M', class_id: '', parent_phone: '', student_type: 'quranic', is_boarding: false });
    setEditId(null);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La photo ne doit pas depasser 5 Mo');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let studentId = editId;
      if (editId) {
        await schoolApi.updateStudent(editId, form);
        toast.success('Eleve mis a jour');
      } else {
        const res = await schoolApi.createStudent(form);
        studentId = res.data.id;
        toast.success('Eleve ajoute');
      }

      if (photoFile && studentId) {
        await schoolApi.uploadStudentPhoto(studentId, photoFile);
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleQuickPhotoUpload = async (studentId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 Mo'); return; }
    setUploadingPhoto(studentId);
    try {
      await schoolApi.uploadStudentPhoto(studentId, file);
      toast.success('Photo mise a jour');
      loadData();
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleEdit = (s) => {
    setForm({
      first_name: s.first_name, last_name: s.last_name, matricule: s.matricule || '',
      birth_date: s.birth_date || '', gender: s.gender, class_id: s.class_id || '',
      parent_phone: s.parent_phone || '', student_type: s.student_type || 'quranic',
      is_boarding: s.is_boarding || false,
    });
    setEditId(s.id);
    setPhotoPreview(s.photo_url ? `${API_URL}${s.photo_url}` : null);
    setDialogOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await schoolApi.deleteStudent(id);
      toast.success('Eleve supprime');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const filtered = students.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
      || (s.matricule || '').toLowerCase().includes(q)
      || (s.parent_phone || '').includes(q);
  });

  const getClassName = (id) => classes.find(c => c.id === id)?.name || '-';

  const getStudentEvalCount = (studentId) => evaluations.filter(e => e.student_id === studentId).length;
  const getStudentLastEval = (studentId) => {
    const evals = evaluations.filter(e => e.student_id === studentId).sort((a, b) => b.evaluation_date?.localeCompare(a.evaluation_date));
    return evals[0] || null;
  };

  const StudentAvatar = ({ student, size = 'sm' }) => {
    const s = size === 'sm' ? 'w-9 h-9' : 'w-16 h-16';
    const iconS = size === 'sm' ? 'w-4 h-4' : 'w-7 h-7';
    if (student.photo_url) {
      return <img src={`${API_URL}${student.photo_url}`} alt="" className={`${s} rounded-full object-cover border border-stone-200`} />;
    }
    return (
      <div className={`${s} rounded-full bg-stone-100 flex items-center justify-center`}>
        <User className={`${iconS} text-stone-400`} />
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Eleves</h1>
          <p className="text-sm text-stone-500">{students.length} eleve(s) {filterClass !== 'all' && `- ${getClassName(filterClass)}`}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-stone-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'list' ? 'bg-white shadow text-stone-900' : 'text-stone-500'}`}
              data-testid="view-list-btn"
            >
              Liste
            </button>
            <button
              onClick={() => setView('progression')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'progression' ? 'bg-white shadow text-stone-900' : 'text-stone-500'}`}
              data-testid="view-progression-btn"
            >
              Progression
            </button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editId ? 'Modifier l\'eleve' : 'Nouvel eleve'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Photo */}
                <div className="flex justify-center">
                  <div className="relative group">
                    {photoPreview ? (
                      <div className="relative">
                        <img src={photoPreview} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-stone-200" />
                        <button
                          type="button"
                          onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                          data-testid="remove-photo-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-20 h-20 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
                        data-testid="photo-upload-btn"
                      >
                        <Camera className="w-5 h-5" />
                        <span className="text-[10px] mt-0.5">Photo</span>
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handlePhotoSelect}
                      data-testid="photo-file-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Prenom *</Label><Input value={form.first_name} onChange={set('first_name')} required data-testid="student-firstname" /></div>
                  <div><Label>Nom *</Label><Input value={form.last_name} onChange={set('last_name')} required data-testid="student-lastname" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Matricule</Label><Input value={form.matricule} onChange={set('matricule')} data-testid="student-matricule" /></div>
                  <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date} onChange={set('birth_date')} data-testid="student-birthdate" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Genre</Label>
                    <Select value={form.gender} onValueChange={set('gender')}>
                      <SelectTrigger data-testid="student-gender"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="M">Masculin</SelectItem><SelectItem value="F">Feminin</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Classe</Label>
                    <Select value={form.class_id} onValueChange={set('class_id')}>
                      <SelectTrigger data-testid="student-class"><SelectValue placeholder="Choisir" /></SelectTrigger>
                      <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tel. parent</Label>
                    <Input value={form.parent_phone} onChange={set('parent_phone')} data-testid="student-phone" />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.student_type} onValueChange={set('student_type')}>
                      <SelectTrigger data-testid="student-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quranic">Coranique</SelectItem>
                        <SelectItem value="classical">Classique</SelectItem>
                        <SelectItem value="both">Mixte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="student-submit">
                  {editId ? 'Modifier' : 'Ajouter'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, matricule ou telephone..."
            className="pl-9 h-9"
            data-testid="search-students"
          />
        </div>
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-48 h-9" data-testid="filter-class">
            <SelectValue placeholder="Toutes les classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les classes</SelectItem>
            {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {filterClass !== 'all' && (
          <Button variant="ghost" size="sm" onClick={() => setFilterClass('all')} className="h-9 text-xs text-stone-500" data-testid="clear-filter-btn">
            Effacer le filtre
          </Button>
        )}
        <span className="text-xs text-stone-400 ml-auto">{filtered.length} resultat(s)</span>
      </div>

      {/* ===== LIST VIEW ===== */}
      {view === 'list' && (
        loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border-stone-200"><CardContent className="p-12 text-center text-stone-400">Aucun eleve</CardContent></Card>
        ) : (
          <Card className="border-stone-200">
            <div className="divide-y divide-stone-100">
              {filtered.map(s => (
                <div key={s.id} className="p-3 flex items-center justify-between hover:bg-stone-50 transition-colors" data-testid={`student-row-${s.id}`}>
                  <div className="flex items-center gap-3">
                    {/* Avatar with quick upload */}
                    <label className="relative cursor-pointer group">
                      <StudentAvatar student={s} />
                      <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        {uploadingPhoto === s.id ? (
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        ) : (
                          <Camera className="w-3.5 h-3.5 text-white" />
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => handleQuickPhotoUpload(s.id, e.target.files[0])}
                        data-testid={`quick-photo-${s.id}`}
                      />
                    </label>
                    <div>
                      <p className="text-sm font-medium text-stone-900">{s.first_name} {s.last_name}</p>
                      <p className="text-xs text-stone-400">
                        {getClassName(s.class_id)}
                        {s.matricule ? ` - ${s.matricule}` : ''}
                        {s.parent_phone ? ` - ${s.parent_phone}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">{s.gender}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} className="h-7 w-7 p-0 text-stone-400" data-testid={`edit-student-${s.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id, `${s.first_name} ${s.last_name}`)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-student-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )
      )}

      {/* ===== PROGRESSION VIEW ===== */}
      {view === 'progression' && (
        loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border-stone-200"><CardContent className="p-12 text-center text-stone-400">Aucun eleve</CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(s => {
              const evalCount = getStudentEvalCount(s.id);
              const lastEval = getStudentLastEval(s.id);
              return (
                <Card key={s.id} className="border-stone-200 hover:shadow-md transition-shadow" data-testid={`progression-card-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <StudentAvatar student={s} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-900 truncate">{s.first_name} {s.last_name}</p>
                        <p className="text-xs text-stone-400">{getClassName(s.class_id)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-stone-600">{evalCount} eval(s)</span>
                      </div>
                      {lastEval && (
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${lastEval.status === 'suivi' ? 'bg-emerald-500' : lastEval.status === 'non_suivi' ? 'bg-red-500' : 'bg-stone-400'}`} />
                          <span className="text-stone-500">
                            {lastEval.eval_type} - {lastEval.status === 'suivi' ? 'Suivi' : lastEval.status === 'non_suivi' ? 'Non suivi' : lastEval.status}
                          </span>
                        </div>
                      )}
                    </div>
                    {lastEval && (
                      <p className="text-[10px] text-stone-400 mt-2">Derniere eval: {lastEval.evaluation_date}</p>
                    )}
                    {!lastEval && (
                      <p className="text-[10px] text-stone-400 mt-2">Aucune evaluation</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
